import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { StarMark } from "@/components/Logo";
import { waLink, MESSAGES, IMAGES } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="inicio" ref={ref} className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-32">
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-[#D4AF37]/10 blur-[140px]"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0A0B] to-transparent" />

      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="relative z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-4 py-2"
          >
            <StarMark className="h-3.5 w-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F9F8F6] sm:text-xs">
              Extensões Premium &amp; Cabelos Naturais
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl"
          >
            <Line delay={0.5}>SEU CABELO</Line>
            <Line delay={0.65}>
              <span className="italic text-gold-gradient">DOS SONHOS</span>
            </Line>
            <Line delay={0.8}>COMEÇA AQUI!</Line>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#A1A09E] sm:text-lg"
          >
            Extensões capilares, cabelo humano, perucas e topos para realçar sua
            beleza e elevar sua autoestima.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              href={waLink(MESSAGES.hero)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-white/20 bg-[#0A0A0B] px-8 py-4 text-xs font-extrabold uppercase tracking-[0.2em] text-[#F9F8F6] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_20px_45px_-12px_rgba(0,0,0,0.9)] transition-all duration-300 hover:-translate-y-0.5 hover:border-white/35 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_28px_55px_-12px_rgba(0,0,0,0.95)] active:translate-y-0"
            >
              <span className="absolute inset-0 -translate-x-full bg-white/10 transition-transform duration-500 group-hover:translate-x-full" />
              <WhatsAppIcon className="h-5 w-5" />
              Quero conhecer as opções
            </a>
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A1A09E]">
              <ArrowDown className="h-4 w-4 animate-bounce text-[#D4AF37]" />
              Sobradinho — DF
            </span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.55 }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-3 rounded-3xl border border-[#D4AF37]/30" />
            <div className="pointer-events-none absolute -inset-10 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
              <motion.img
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: EASE }}
                src={IMAGES.fachada}
                alt="Fachada completa do Salão Estrela em Sobradinho, Brasília/DF"
                data-testid="hero-main-photo"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
